import { isWebEnvironment, isReactNativeEnvironment, getTargetWindow, shouldLog } from './platform';

let signalRHubConnectionFunc;

if (isReactNativeEnvironment) {
  global.window = global.window || {};
  if (typeof global.navigator === 'undefined') {
    global.navigator = {};
  }
}

const makeSureDocument = () => {
  if (isReactNativeEnvironment) {
    const targetWindow = global.window;
    const originalDocument = targetWindow.document;

    targetWindow.document = targetWindow.document || {
      readyState: 'complete',
    };
    if (!targetWindow.document.readyState) {
      targetWindow.document.readyState = 'complete';
    }

    return () => {
      targetWindow.document = originalDocument;
    };
  }
  return () => {};
};

if (isWebEnvironment || isReactNativeEnvironment) {
  const targetWindow = getTargetWindow();
  
  targetWindow._signalRDebugLoggingEnabled = targetWindow._signalRDebugLoggingEnabled || false;
  targetWindow._signalRLoggingLevel = targetWindow._signalRLoggingLevel || 'none';
  
  if (!targetWindow.addEventListener) {
    targetWindow.addEventListener = () => {};
  }

  if (!targetWindow.navigator) {
    targetWindow.navigator = {};
  }

  if (isReactNativeEnvironment && (!targetWindow.navigator.userAgent || targetWindow.navigator.userAgent === '')) {
    targetWindow.navigator.userAgent = "react-native";
  }

  if (isReactNativeEnvironment && typeof global.WebSocket !== 'undefined' && !targetWindow.WebSocket) {
    class ReactNativeWebSocket extends global.WebSocket {
      constructor(url, protocols) {
        if (shouldLog()) console.log('SignalR: Creating WebSocket connection to:', url);
        super(url, protocols);
        
        const originalOnError = this.onerror;
        this.onerror = (event) => {
          if (shouldLog()) console.error('SignalR WebSocket Error:', { readyState: this.readyState, url: this.url, event });
          if (originalOnError) originalOnError.call(this, event);
        };
        
        const originalOnClose = this.onclose;
        this.onclose = (event) => {
          if (shouldLog()) console.log('SignalR WebSocket Closed:', { code: event.code, reason: event.reason, wasClean: event.wasClean });
          if (originalOnClose) originalOnClose.call(this, event);
        };
        
        const originalOnOpen = this.onopen;
        this.onopen = (event) => {
          if (shouldLog()) console.log('SignalR WebSocket Opened successfully');
          if (originalOnOpen) originalOnOpen.call(this, event);
        };
      }
    }
    
    targetWindow.WebSocket = ReactNativeWebSocket;
  }

  targetWindow.jQuery = require("./signalr-jquery-polyfill.js").default;
}

const signalR = {
  setLogger: logger => {
    const targetWindow = getTargetWindow();
    
    if (!targetWindow.console) {
      targetWindow.console = {};
    }
    const originalDebug = targetWindow.console.debug;
    targetWindow.console.debug = logger;
    return () => (targetWindow.console.debug = originalDebug);
  },
  hubConnection: (serverUrl, options) => {
    const revertDocument = makeSureDocument();
    const targetWindow = getTargetWindow();

    try {
      if (!signalRHubConnectionFunc) {
        try {
          require('./jquery.signalR.js');

          const $ = targetWindow.jQuery;

          if (!$) {
            throw new Error('jQuery not found after loading signalR library');
          }

          if ($.hubConnection) {
            signalRHubConnectionFunc = $.hubConnection;
          } else if ($.connection && $.connection.hub) {
            signalRHubConnectionFunc = $.connection.hub;
          } else if ($ && typeof $.fn === 'object') {
            signalRHubConnectionFunc = $.fn.hubConnection || $.prototype.hubConnection;
          }

          if (!signalRHubConnectionFunc) {
            throw new Error('hubConnection not found on jQuery object');
          }
        } catch (error) {
          console.error('Failed to load SignalR library:', error);
          throw new Error(`SignalR initialization failed: ${error.message}`);
        }
      }

      const [protocol, host] = serverUrl.split(/\/\/|\//);
      if (options && options.headers) {
        targetWindow.jQuery.defaultAjaxHeaders = options.headers;
      }

      if (typeof signalRHubConnectionFunc !== 'function') {
        throw new Error('signalRHubConnectionFunc is not a function');
      }

      const hubConnectionFunc = signalRHubConnectionFunc(serverUrl, options);
      const originalStart = hubConnectionFunc.start;

      hubConnectionFunc.start = (options, ...args) => {
        const revertDocument = makeSureDocument();
        const targetWindow = getTargetWindow();

        try {
          if (isReactNativeEnvironment) {
            targetWindow.document.createElement = () => {
              return {
                protocol,
                host,
              };
            };
          }

          if (isReactNativeEnvironment && targetWindow.location === undefined) {
            try {
              targetWindow.location = {
                protocol: protocol || 'https:',
                host: host || 'localhost',
                href: serverUrl,
              };
            } catch (error) {
              console.warn('Could not set location object:', error);
            }
          }

          return originalStart.call(hubConnectionFunc, options, ...args);
        } finally {
          revertDocument();
        }
      };

      return hubConnectionFunc;
    } finally {
      revertDocument();
    }
  },
};

export default signalR;
